import React from "react";

export function PageHeader({ title, subtitle, overline, actions, testId }) {
  return (
    <div
      data-testid={testId}
      className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 pb-6 border-b border-[#e4e4e7]"
    >
      <div>
        {overline ? <div className="phizo-label mb-2">{overline}</div> : null}
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-[#09090b]">
          {title}
        </h1>
        {subtitle ? (
          <p className="text-sm text-[#52525b] mt-2 max-w-2xl">{subtitle}</p>
        ) : null}
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

export function MetricCard({ label, value, delta, deltaLabel, accent = "default", testId, onClick, to }) {
  const accentMap = {
    default: "text-[#09090b]",
    danger: "text-[#FF2A2A]",
    warning: "text-[#B8860B]",
    success: "text-[#00A142]",
    primary: "text-[#002FA7]",
  };
  const clickable = !!(onClick || to);
  const cls = `phizo-card p-5 ${clickable ? "cursor-pointer hover:border-[#09090b] transition-colors" : ""}`;
  const content = (
    <>
      <div className="phizo-label mb-3">{label}</div>
      <div className={`font-heading text-3xl sm:text-4xl font-black tracking-tighter ${accentMap[accent]}`}>
        {value}
      </div>
      {delta ? (
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#e4e4e7] text-xs text-[#52525b]">
          <span>{deltaLabel}</span>
          <span className="mono font-semibold">{delta}</span>
        </div>
      ) : null}
    </>
  );
  if (clickable) {
    return (
      <button type="button" data-testid={testId} onClick={onClick} className={`text-left ${cls}`}>
        {content}
      </button>
    );
  }
  return <div data-testid={testId} className={cls}>{content}</div>;
}

export function StatusBadge({ status }) {
  const map = {
    pending: { bg: "#FFC000", fg: "#09090b", label: "Pending" },
    approved: { bg: "#00C853", fg: "#09090b", label: "Approved" },
    rejected: { bg: "#FF2A2A", fg: "#fff", label: "Rejected" },
    received: { bg: "#09090b", fg: "#fff", label: "Received" },
    draft: { bg: "#e4e4e7", fg: "#09090b", label: "Draft" },
    sent: { bg: "#002FA7", fg: "#fff", label: "Sent" },
    accepted: { bg: "#00C853", fg: "#09090b", label: "Accepted" },
    converted: { bg: "#09090b", fg: "#fff", label: "Converted" },
    processing: { bg: "#002FA7", fg: "#fff", label: "Processing" },
    fulfilled: { bg: "#00C853", fg: "#09090b", label: "Fulfilled" },
    cancelled: { bg: "#a1a1aa", fg: "#fff", label: "Cancelled" },
    active: { bg: "#00C853", fg: "#09090b", label: "Active" },
    maintenance: { bg: "#FFC000", fg: "#09090b", label: "Maintenance" },
    retired: { bg: "#a1a1aa", fg: "#fff", label: "Retired" },
    operational: { bg: "#00C853", fg: "#09090b", label: "Operational" },
    scheduled: { bg: "#FFC000", fg: "#09090b", label: "Scheduled" },
    down: { bg: "#FF2A2A", fg: "#fff", label: "Down" },
    completed: { bg: "#09090b", fg: "#fff", label: "Completed" },
  };
  const s = map[status] || { bg: "#e4e4e7", fg: "#09090b", label: status };
  return (
    <span
      className="phizo-status"
      style={{ background: s.bg, color: s.fg, borderColor: s.bg }}
    >
      {s.label}
    </span>
  );
}

export function PriorityBadge({ priority }) {
  const map = {
    low: { bg: "#e4e4e7", fg: "#09090b" },
    medium: { bg: "#002FA7", fg: "#fff" },
    high: { bg: "#FFC000", fg: "#09090b" },
    urgent: { bg: "#FF2A2A", fg: "#fff" },
  };
  const s = map[priority] || map.low;
  return (
    <span className="phizo-status" style={{ background: s.bg, color: s.fg, borderColor: s.bg }}>
      {priority}
    </span>
  );
}

export function Empty({ label = "No records yet." }) {
  return (
    <div className="phizo-surface p-10 text-center">
      <div className="phizo-label mb-2">Empty</div>
      <div className="font-heading text-xl font-bold text-[#09090b]">{label}</div>
    </div>
  );
}

export function Modal({ open, onClose, title, children, wide = false, size, actions, testId }) {
  if (!open) return null;
  const widthClass = size === "lg" ? "max-w-3xl"
                    : size === "xl" ? "max-w-5xl"
                    : (wide ? "max-w-3xl" : "max-w-lg");
  return (
    <div
      data-testid={testId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
      onClick={onClose}
    >
      <div
        className={`bg-white border border-[#e4e4e7] w-full ${widthClass} max-h-[90vh] overflow-y-auto flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-[#e4e4e7] shrink-0">
          <div>
            <div className="phizo-label">Dialog</div>
            <div className="font-heading text-xl font-bold text-[#09090b] mt-1">{title}</div>
          </div>
          <button
            onClick={onClose}
            data-testid="modal-close"
            className="text-[#52525b] hover:text-[#09090b] text-2xl leading-none"
          >
            ×
          </button>
        </div>
        <div className="p-5 overflow-y-auto flex-1">{children}</div>
        {actions && (
          <div className="flex justify-end items-center gap-2 p-4 border-t border-[#e4e4e7] bg-[#fafafa] shrink-0">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

export function Field({ label, children, hint }) {
  return (
    <div>
      <label className="phizo-label block mb-1.5">{label}</label>
      {children}
      {hint ? <div className="text-xs text-[#a1a1aa] mt-1">{hint}</div> : null}
    </div>
  );
}

export function Input(props) {
  return (
    <input
      {...props}
      className={`w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none disabled:bg-[#f4f4f5] ${props.className || ""}`}
    />
  );
}

export function Select({ children, ...props }) {
  return (
    <select
      {...props}
      className={`w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none ${props.className || ""}`}
    >
      {children}
    </select>
  );
}

export function Textarea(props) {
  return (
    <textarea
      {...props}
      className={`w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 outline-none min-h-[80px] ${props.className || ""}`}
    />
  );
}

export function BtnPrimary({ children, ...props }) {
  return (
    <button {...props} className={`phizo-btn-primary ${props.className || ""}`}>
      {children}
    </button>
  );
}

export function BtnSecondary({ children, ...props }) {
  return (
    <button
      {...props}
      className={`border border-[#e4e4e7] bg-white px-4 py-2 text-sm font-medium text-[#09090b] hover:bg-[#f4f4f5] transition-colors disabled:opacity-50 ${props.className || ""}`}
    >
      {children}
    </button>
  );
}

export function BtnDanger({ children, ...props }) {
  return (
    <button
      {...props}
      className={`bg-[#FF2A2A] text-white px-4 py-2 text-sm font-semibold hover:bg-[#d61f1f] transition-colors disabled:opacity-50 ${props.className || ""}`}
    >
      {children}
    </button>
  );
}

export function BtnGhost({ children, ...props }) {
  return (
    <button
      {...props}
      className={`px-3 py-1.5 text-xs font-semibold text-[#52525b] hover:text-[#09090b] hover:bg-[#f4f4f5] transition-colors ${props.className || ""}`}
    >
      {children}
    </button>
  );
}
